<template>
  <div class="terminal-window" :class="{ minimized: isMinimized }" v-if="!isClosed">
    <div class="terminal-header">
      <div class="terminal-title">
        <span class="terminal-icon">⬛</span>
        {{ title }}
      </div>
      <div class="terminal-controls">
        <button @click="handleMinimize" class="control-btn minimize">−</button>
        <button @click="handleMaximize" class="control-btn maximize">□</button>
        <button @click="handleClose" class="control-btn close">×</button>
      </div>
    </div>
    <div class="terminal-body" v-if="!isMinimized">
      <div class="terminal-content">
        <div class="terminal-prompt">{{ prompt }}</div>
        <div class="terminal-text">
          <p v-if="command">{{ prompt }}{{ command }}</p>
          <br v-if="command">
          <div class="content-section">
            <div class="terminal-display">{{ displayedContent }}<span v-if="isTyping" class="typing-cursor">_</span></div>
          </div>
          <br>
          <div class="terminal-cursor" v-if="!isTyping">_</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  title: {
    type: String,
    default: 'Command Prompt'
  },
  prompt: {
    type: String,
    default: 'C:\\Users\\User>'
  },
  command: {
    type: String,
    default: ''
  },
  content: {
    type: String,
    default: ''
  },
  isMinimized: {
    type: Boolean,
    default: false
  },
  isClosed: {
    type: Boolean,
    default: false
  },
  typingSpeed: {
    type: Number,
    default: 20
  }
})

const emit = defineEmits(['minimize', 'maximize', 'close', 'typing-complete'])

const displayedContent = ref('')
const isTyping = ref(false)
const typingInterval = ref(null)

const startTypingEffect = () => {
  if (!props.content) return
  
  displayedContent.value = ''
  isTyping.value = true
  let currentIndex = 0
  
  typingInterval.value = setInterval(() => {
    if (currentIndex < props.content.length) {
      displayedContent.value += props.content[currentIndex]
      currentIndex++
    } else {
      clearInterval(typingInterval.value)
      isTyping.value = false
      emit('typing-complete')
    }
  }, props.typingSpeed)
}

const stopTypingEffect = () => {
  if (typingInterval.value) {
    clearInterval(typingInterval.value)
    typingInterval.value = null
  }
  displayedContent.value = props.content
  isTyping.value = false
}

const hasBeenClosed = ref(false)

// Watch for when terminal becomes visible after being closed
watch(() => props.isClosed, (newIsClosed, oldIsClosed) => {
  // If terminal was closed and is now being shown
  if (oldIsClosed === true && newIsClosed === false) {
    hasBeenClosed.value = true
    if (props.content) {
      startTypingEffect()
    }
  }
})

// Watch for when terminal is minimized/unminimized
watch(() => props.isMinimized, (newIsMinimized, oldIsMinimized) => {
  // If terminal was minimized and is now being shown
  if (oldIsMinimized === true && newIsMinimized === false && !hasBeenClosed.value) {
    // Show content immediately without typing effect
    displayedContent.value = props.content
    isTyping.value = false
  }
})

// Watch for content changes
watch(() => props.content, (newContent) => {
  if (newContent) {
    // If this is the first time content is loaded, start typing
    if (displayedContent.value === '' && !hasBeenClosed.value) {
      startTypingEffect()
    } else if (hasBeenClosed.value) {
      // If terminal was reopened after being closed, restart typing
      stopTypingEffect()
      startTypingEffect()
      hasBeenClosed.value = false // Reset after typing starts
    } else {
      // Otherwise just show content immediately
      displayedContent.value = newContent
    }
  }
})

// Add keyboard event listener for spacebar
const handleKeyPress = (event) => {
  if (event.code === 'Space' && isTyping.value) {
    event.preventDefault()
    stopTypingEffect()
  }
}

// Start typing effect on mount if content exists
onMounted(() => {
  if (props.content) {
    startTypingEffect()
  }
  // Add global keyboard listener
  window.addEventListener('keydown', handleKeyPress)
})

// Cleanup on unmount
onUnmounted(() => {
  stopTypingEffect()
  window.removeEventListener('keydown', handleKeyPress)
})

const handleMinimize = () => {
  emit('minimize')
}

const handleMaximize = () => {
  emit('maximize')
}

const handleClose = () => {
  stopTypingEffect()
  hasBeenClosed.value = false // Reset state when closing
  emit('close')
}

// Expose method to allow parent to skip typing animation
defineExpose({
  skipTyping: stopTypingEffect,
  startTyping: startTypingEffect
})
</script>

<style scoped>
.terminal-window {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  height: 500px;
  background: #0c0c0c;
  border: 1px solid #404040;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  z-index: 100;
  transition: all 0.3s ease;
}

.terminal-window.minimized {
  transform: translate(-50%, 100%);
  opacity: 0;
}

.terminal-window.maximized {
  width: 90vw;
  height: 80vh;
  top: 10vh;
  left: 5vw;
  transform: none;
}

.terminal-header {
  background: #2d2d2d;
  color: white;
  padding: 8px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px 8px 0 0;
}

.terminal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.terminal-controls {
  display: flex;
  gap: 8px;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.control-btn {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
}

.minimize {
  background: #4caf50;
  color: white;
}

.maximize {
  background: #fdd835;
  color: #333;
}

.close {
  background: #f44336;
  color: white;
}

.control-btn:hover {
  opacity: 0.8;
}

.terminal-body {
  height: calc(100% - 40px);
  padding: 16px;
  overflow: hidden;
}

.terminal-content {
  color: #00ff00;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 14px;
  line-height: 1.4;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.terminal-prompt {
  color: #ffffff;
  margin-bottom: 8px;
}

.terminal-text p {
  margin: 0;
  color: #cccccc;
}

.content-section {
  flex: 1;
  margin: 16px 0;
}

.terminal-display {
  color: #00ff00;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
  height: 100%;
  overflow-y: auto;
  padding-right: 8px;
}

.terminal-display::-webkit-scrollbar {
  width: 8px;
}

.terminal-display::-webkit-scrollbar-track {
  background: #1a1a1a;
}

.terminal-display::-webkit-scrollbar-thumb {
  background: #404040;
  border-radius: 4px;
}

.terminal-display::-webkit-scrollbar-thumb:hover {
  background: #505050;
}

.terminal-cursor {
  color: #00ff00;
  animation: blink 1s infinite;
}

.typing-cursor {
  color: #00ff00;
  animation: blink 0.5s infinite;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

@media (max-width: 768px) {
  .terminal-window {
    width: 90vw;
    height: 60vh;
  }
}
</style>